import React, { useEffect, useState } from "react";
import Layout from "../Componants/Layout";
import { Col, Container, Row } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Form from "react-bootstrap/Form";
import * as Yup from "yup";
import { Field, Formik } from "formik";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRequest, postRequest, putRequest } from "../services/apiMethods";
import Select from "react-select";
import useVendorDetails from "../hooks/VendorDetails";
import useImageUploader from "../hooks/UploadImage";
import { PortfolioDetailvalidationSchema } from "../utils/Validations";
import ConfirmModal from "../Modal/ConfirmModal";
import toast from "react-hot-toast";

const customStyles = {
  control: (provided) => ({
    ...provided,
    height: 45, // Set the height
    boxShadow: 'none',
    '&:hover': {
      borderColor: '',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    height: 45, // Set the height for each option
    padding: '5px 10px', // Adjust padding to ensure text fits within the height
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: 4,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#333',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#777',
  }),
};

export default function PortfolioDetail() {
  const location = useLocation();
  const [uploading, setUploading] = useState(false);
  const { data, isLoading } = useVendorDetails();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedImages, setSelectedImages] = useState([]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("");

  const handleDelete = (type) => {
    setModalOpen(true);
    setSelectedType(type);
  };

  const handleConfirmClose = async () => {
    setModalOpen(false);
  };

  const { uploadImage, uploadedUrl, loading, error, errorMessage } = useImageUploader(); // Use the custom hook

  const { data: categoryList, error: queryError, isLoading: load } = useQuery({
    queryKey: ["categories"],
    queryFn: () => getRequest("/auth/view-category"),
  });

  const { data: PortfolioDetails, error: PortfolioError, isLoading: PortfolioLoading } = useQuery({
    queryKey: ["PortfolioDetails"],
    queryFn: () => getRequest(`/portfolio/details${location?.search}`),
    enabled: !!location?.search,
  });


  useEffect(() => {
    if (!location?.search) {
      queryClient.setQueryData(["PortfolioDetails"], null);
    }
  }, [location?.search, queryClient]);

  const filteredCategories = Array.isArray(categoryList?.data)
    ? categoryList.data.filter((category) =>
      data?.vendorServices.some(
        (vendorService) =>
          vendorService.category === category.categoryId &&
          vendorService.services.includes(category.serviceId)
      )
    )
    : [];

  // Map filtered categories to options for Select component
  const options = filteredCategories.map((item) => ({
    value: item.serviceId,
    label: item.serviceName,
    category: item.categoryId, // Assuming categoryId is the category identifier
  }));

  useEffect(() => {
    if (data) {
      console.log("Vendor data loaded:", data);
    }
  }, [data]);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);

    setUploading(true);

    // Create an array of promises that upload the images
    const uploadPromises = files.map((file) => uploadImage(file));

    try {
      // Wait for all uploads to finish
      const uploadedUrls = await Promise.all(uploadPromises);

      // Update the state with all successfully uploaded URLs
      setSelectedImages((prevImages) => [...prevImages, ...uploadedUrls.filter(Boolean)]);  // Ensure to filter out any undefined/null values

    } catch (err) {
      console.error("Error uploading images", err);
      toast.error("Error uploading some images.");
    } finally {
      setUploading(false);
    }
  };

  const { mutate, isLoading: apiState } = useMutation({
    mutationFn: (data) => {


      if (location?.search) {
        const searchParams = new URLSearchParams(location.search);
        const id = searchParams.get('id'); // Get the 'id' query param
        const modifyData = {
          id: id,
          projectName: data?.projectName || "",
          // category: data?.category || "",
          description: data?.description || "",
          image: selectedImages || [],
          // vendorCategory: data?.vendorCategory || PortfolioDetails?.data?.vendorCategory,
        };
        return putRequest(`/portfolio/edit`, modifyData);
      } else {

        const modifyData = {
          projectName: data?.projectName || "",
          // category: data?.category || "",
          description: data?.description || "",
          image: selectedImages || [],
          // vendorCategory: data?.vendorCategory || "",
        };

        return postRequest(`/portfolio/create`, modifyData);
      }
    },
    onSuccess: (data) => {
      toast.success(`${data?.message}`, {
        iconTheme: {
          primary: '#337FE0',
          secondary: '#FFFAEE',
        },
      })
      navigate(-1);
    },
    onError: (error) => {
      console.error("Error during API call:", error);
    },
  });

  useEffect(() => {
    if (uploadedUrl) {
      setSelectedImages((prevImages) => [...prevImages, uploadedUrl]);
    }
  }, [uploadedUrl]);


  useEffect(() => {
    if (PortfolioDetails?.data && location?.search) {
      setSelectedImages(PortfolioDetails?.data?.image || []);
    }
  }, [PortfolioDetails]);

  return (
    <Layout>
      <Container>
        <div className="dashboard-main">
          <h3
            className="with-back-btn cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M4 12L3.29289 11.2929L2.58579 12L3.29289 12.7071L4 12ZM19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11V13ZM9.29289 5.29289L3.29289 11.2929L4.70711 12.7071L10.7071 6.70711L9.29289 5.29289ZM3.29289 12.7071L9.29289 18.7071L10.7071 17.2929L4.70711 11.2929L3.29289 12.7071ZM4 13H19V11H4V13Z"
                fill="black"
              />
            </svg>
            Portfolio
          </h3>
          <Row>
            <Col lg={8}>
              <div className="my-profile-card-contain">
                <div className="my-profile-title">
                  <h4>Portfolio Detail</h4>
                </div>
                <div className="my-profile-box-contain">
                  <div className="my-profile-box-form">
                    <Formik
                      initialValues={{
                        projectName: PortfolioDetails?.data?.projectName || "",
                        // category: PortfolioDetails?.data?.category || [],
                        category: (PortfolioDetails?.data?.category || []).map((catId) => {
                          return options.find((option) => option.value === catId) || null; // Find the option by serviceId
                        }),
                        image: PortfolioDetails?.data?.image || [],
                        description: PortfolioDetails?.data?.description || "",
                      }}
                      enableReinitialize={true}
                      validationSchema={PortfolioDetailvalidationSchema}
                      onSubmit={async (values) => {
                        const categoriesFilter = values.category.map(
                          (option) => option.value
                        );
                        const payload = { ...values, category: categoriesFilter, image: selectedImages };
                        mutate(payload);
                      }}
                    >
                      {({
                        values,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                        errors,
                        touched,
                      }) => (
                        <Form onSubmit={handleSubmit} className="cmn-form-set">
                          <Form.Group className="mb-3" controlId="formBasicPassword">
                            <Form.Label>Project Name</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Type here"
                              name="projectName"
                              value={values.projectName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.projectName && !!errors.projectName}
                            />
                            {touched.projectName && errors.projectName && (
                              <Form.Control.Feedback type="invalid">
                                {errors.projectName}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                          {/* <Form.Label>Category</Form.Label>
                          <Field name="category">
                            {() => (
                              <Select
                                options={options}
                                styles={customStyles}
                                className="min-w-200 set-height"
                                isMulti

                                value={values.category}
                                onChange={(selectedOptions) => {
                                  setFieldValue("category", selectedOptions || []);
                                  const groupedServices = selectedOptions.reduce(
                                    (acc, option) => {
                                      const category = option.category;
                                      if (!acc[category]) {
                                        acc[category] = { category, services: [] };
                                      }
                                      acc[category].services.push(option.value);
                                      return acc;
                                    },
                                    {}
                                  );

                                  const vendorService = Object.values(groupedServices).map((group) => ({
                                    category: group.category,
                                    services: group.services,
                                  }));

                                  setFieldValue("vendorCategory", vendorService);
                                }}
                                placeholder="Search here"
                              />
                            )}
                          </Field>

                          {errors.category && touched.category && (
                            <div className="error-message">{errors.category}</div>
                          )} */}

                          <div className="portfolio-detail-gallery">
                            <div className="cmpny-logo-upld">
                              <Form.Control
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                accept="image/*"
                              />
                              <button className="white-btn m-0">Upload Image</button>
                            </div>
                            <div className="portfolio-gellery-img">
                              {selectedImages.length > 0
                                ? selectedImages.map((src, index) => (
                                  <div className="pdf-image" key={index} style={{ position: 'relative', display: 'inline-block', marginRight: '10px' }}>
                                    <img
                                      src={src}
                                      alt={`uploaded-img-${index}`}
                                      style={{ width: '100px', height: '100px', borderRadius: '5px' }}
                                    />

                                    <img className="cross-icon" onClick={() => {
                                      setSelectedImages((prevImages) =>
                                        prevImages.filter((image, imgIndex) => imgIndex !== index)
                                      );
                                    }}
                                      src={require("../assets/images/crossIcon.png")}
                                      alt="Employee"
                                    />

                                  </div>
                                ))
                                : null}
                            </div>
                          </div>
                          <Form.Group className="mb-3" controlId="formBasicPassword">
                            <Form.Label>Describe the project</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={6}
                              placeholder="Type here"
                              name="description"
                              value={values.description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.description && !!errors.description}
                            />
                            {touched.description && errors.description && (
                              <Form.Control.Feedback type="invalid">
                                {errors.description}
                              </Form.Control.Feedback>
                            )}
                            <Form.Text className="text-muted">
                              {/* Minimum 50 characters */}
                            </Form.Text>
                          </Form.Group>

                          <div className="d-flex gap-4" >
                            <button type="submit" className="theme-comm-btn">
                              Save
                            </button>
                            {location.search &&
                              <button type="button" className="theme-red-comm-btn" onClick={() => handleDelete("portfolio")}>Delete</button>}
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={4}></Col>
          </Row>
        </div>
      </Container>

      <ConfirmModal
        data={location.search}
        isModalOpen={isModalOpen}
        selectedType={selectedType}
        handleConfirmClose={handleConfirmClose}
      />

    </Layout>
  );
}

